import { Button } from "@/components/ui/button";
import type { ExpenseSource } from "../types";
import { bankLabels, formatSEK } from "../lib/format";

interface ExpenseSourceSelectorProps {
  sources: ExpenseSource[];
  selectedSourceIds: string[];
  defaultSourceIds: string[];
  onChange: (sourceIds: string[]) => void;
}

export function ExpenseSourceSelector({
  sources,
  selectedSourceIds,
  defaultSourceIds,
  onChange,
}: ExpenseSourceSelectorProps) {
  const selected = new Set(selectedSourceIds);

  function toggle(sourceId: string) {
    const next = new Set(selected);
    if (next.has(sourceId)) {
      next.delete(sourceId);
    } else {
      next.add(sourceId);
    }
    onChange([...next]);
  }

  if (sources.length === 0) return null;

  const includedCount = sources.filter((source) => selected.has(source.id)).length;

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Källor som räknas i totalsummor</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Som standard räknas utgifter från de stora svenska bankerna. Alla andra banker/kort visas fortfarande i appen men markeras som “visas bara” tills du väljer dem här.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onChange(defaultSourceIds)}>
            Standard
          </Button>
          <Button variant="outline" size="sm" onClick={() => onChange(sources.map((source) => source.id))}>
            Alla
          </Button>
          <Button variant="outline" size="sm" onClick={() => onChange([])}>
            Inga
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {sources.map((source) => {
          const checked = selected.has(source.id);
          return (
            <label
              key={source.id}
              className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                checked
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-muted/30 text-muted-foreground hover:bg-muted/60"
              }`}
              title={`${source.label}: ${formatSEK(source.totalExpenses)} i utgifter`}
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={checked}
                onChange={() => toggle(source.id)}
              />
              <span className="font-medium">
                {bankLabels[source.bank] ?? source.bank}
              </span>
              {source.label !== source.bank && (
                <span className="max-w-[160px] truncate opacity-80">
                  {source.label.replace(`${source.bank} · `, "")}
                </span>
              )}
              <span className="tabular-nums opacity-70">
                {source.expenseCount} st
              </span>
              {source.defaultIncluded && (
                <span className="rounded-full bg-success/10 px-1.5 py-0.5 text-[10px] text-success">
                  standard
                </span>
              )}
            </label>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">
        {includedCount} av {sources.length} källor räknas i utgiftsstatistik, budget och diagram.
      </p>
    </div>
  );
}
